import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { formatDate, CORRIDORS, formatAmount } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Repeat, Eye, Bookmark, Search, Inbox, History as HistoryIcon, Plus, ArrowRight, Download } from "lucide-react";
import CopyRefButton from "@/components/CopyRefButton";
import TransactionDetailModal from "@/components/TransactionDetailModal";
import ImageLightbox from "@/components/ImageLightbox";

export default function MyTransactions() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailCode, setDetailCode] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login"); return; }
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/me/transactions");
        setItems(Array.isArray(data) ? data : []);
      } finally { setLoading(false); }
    })();
  }, [user, authLoading, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (t.code || "").toLowerCase().includes(q) ||
        (t.beneficiary_name || "").toLowerCase().includes(q) ||
        (t.beneficiary_phone || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, statusFilter]);

  const stats = useMemo(() => ({
    total: items.length,
    pending: items.filter((t) => ["en_attente", "verifie", "envoye"].includes(t.status)).length,
    done: items.filter((t) => t.status === "termine").length,
  }), [items]);

  return (
    <div className="min-h-screen sl-hero-bg">
      <Header />
      <div className="pt-24 sm:pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">Mon historique</div>
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-1 flex items-center gap-2">
                <HistoryIcon className="text-[var(--sl-primary)] flex-shrink-0" size={28} /> Mes envois
              </h1>
              <p className="text-gray-600 mt-1 text-sm">Suivez le statut de toutes vos transactions Swiftlink-Africa.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link
                to="/modeles"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm rounded-full border-2 px-3 sm:px-4 py-1.5 sm:py-2 font-medium border-[var(--sl-primary)] text-[var(--sl-primary)] hover:bg-[var(--sl-primary)] hover:text-white transition"
                data-testid="link-templates"
              >
                <Bookmark size={14} /> Mes modèles
              </Link>
              <Link to="/envoyer" className="sl-btn-primary !text-xs sm:!text-sm !px-3 sm:!px-4" data-testid="new-tx-btn">
                <Plus size={14} /> Nouvel envoi
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          {items.length > 0 && (
            <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3" data-testid="my-tx-stats">
              <StatChip label="Total" value={stats.total} testid="stat-total" />
              <StatChip label="En cours" value={stats.pending} accent testid="stat-pending" />
              <StatChip label="Terminés" value={stats.done} testid="stat-done" />
            </div>
          )}

          {/* Search + filters */}
          {items.length > 0 && (
            <div className="mt-5 flex flex-col sm:flex-row gap-2" data-testid="my-tx-controls">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher code, bénéficiaire, numéro…"
                  className="sl-input !pl-9 !py-2 !text-sm"
                  data-testid="my-tx-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="sl-input !py-2 !text-sm sm:w-48"
                data-testid="my-tx-filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="verifie">Vérifié</option>
                <option value="envoye">Envoyé</option>
                <option value="termine">Terminé</option>
                <option value="annule">Annulé</option>
              </select>
            </div>
          )}

          {/* List */}
          <div className="mt-6">
            {loading ? (
              <div className="sl-card text-center text-sm text-gray-500 py-12" data-testid="my-tx-loading">Chargement…</div>
            ) : items.length === 0 ? (
              <div className="sl-card text-center py-12" data-testid="my-tx-empty">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)] mb-3">
                  <Inbox size={26} />
                </div>
                <h3 className="font-heading font-semibold text-base">Aucun envoi pour l&apos;instant</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">Lancez votre premier transfert vers le Gabon, la France ou le Rwanda en quelques clics.</p>
                <Link to="/envoyer" className="sl-btn-primary mt-5 inline-flex" data-testid="empty-send-cta">
                  Faire un envoi <ArrowRight size={14} />
                </Link>
              </div>
            ) : filtered.length === 0 ? (
              <div className="sl-card text-center text-sm text-gray-500 py-10" data-testid="my-tx-no-match">
                Aucune transaction ne correspond à votre recherche.
              </div>
            ) : (
              <ul className="space-y-3" data-testid="my-tx-list">
                {filtered.map((t) => (
                  <TxCard key={t.id} t={t} onDetail={() => setDetailCode(t.code)} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <Footer />
      {detailCode && <TransactionDetailModal code={detailCode} onClose={() => setDetailCode(null)} />}
    </div>
  );
}

function StatChip({ label, value, accent, testid }) {
  return (
    <div
      className={`rounded-xl px-3 py-2 sm:py-3 ${accent ? "bg-[var(--sl-primary)]/10 border border-[var(--sl-primary)]/30" : "bg-[var(--sl-card-bg)] border border-[color:var(--sl-border)]"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading text-lg sm:text-xl font-bold ${accent ? "text-[var(--sl-primary)]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function TxCard({ t, onDetail }) {
  const cdef = CORRIDORS[t.corridor || "tg_ga"];
  const [receiptOpen, setReceiptOpen] = useState(false);
  return (
    <li className="sl-card sl-card-hoverable !p-4 sm:!p-5" data-testid={`tx-card-${t.code}`}>
      <div className="flex items-start gap-3">
        {/* Corridor avatar */}
        <div
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-lg sm:text-xl flex-shrink-0"
          title={cdef?.label}
          aria-label={cdef?.label}
        >
          <span>{cdef?.flagFrom}</span>
          <span className="text-xs mx-0.5 text-gray-400">→</span>
          <span>{cdef?.flagTo}</span>
        </div>

        {/* Main col */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/suivi?code=${t.code}`} className="font-mono text-sm font-bold text-[var(--sl-primary)]" data-testid={`tx-code-${t.code}`}>{t.code}</Link>
            <CopyRefButton code={t.code} compact />
            <StatusBadge status={t.status} cancellationReason={t.cancellation_reason} />
          </div>
          <div className="mt-1 text-sm sm:text-base font-medium text-gray-900 truncate">{t.beneficiary_name}</div>
          <div className="mt-0.5 text-[11px] sm:text-xs text-gray-500 truncate">
            {cdef?.short} · {formatDate(t.created_at)}
          </div>
        </div>

        {/* Amount */}
        <div className="text-right flex-shrink-0">
          <div className="font-heading font-bold text-sm sm:text-base">{formatAmount(t.amount_xaf, t.target_currency || "XAF")}</div>
          <div className="text-[10px] text-gray-400">≈ {formatAmount(t.amount_xof, t.source_currency || "XOF")}</div>
        </div>
      </div>

      {/* Actions strip — only kept: Détails + Re-envoyer (PDF/WhatsApp moved server-side, admin pushes those) */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onDetail}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] hover:bg-[var(--sl-primary)]/15 font-medium transition"
          data-testid={`detail-${t.code}`}
        >
          <Eye size={12} /> Détails
        </button>
        <Link
          to={`/envoyer?reuse=${t.code}`}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium transition"
          data-testid={`resend-${t.code}`}
        >
          <Repeat size={12} /> Refaire ce transfert
        </Link>
        {t.status === "termine" && (
          <button
            type="button"
            onClick={() => setReceiptOpen(true)}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium transition"
            data-testid={`receipt-${t.code}`}
          >
            <Download size={12} /> Reçu PDF
          </button>
        )}
        <Link
          to={`/suivi?code=${t.code}`}
          className="ml-auto text-xs text-gray-500 hover:text-[var(--sl-primary)] inline-flex items-center gap-1"
          data-testid={`track-${t.code}`}
        >
          Suivi <ArrowRight size={11} />
        </Link>
      </div>
      {receiptOpen && (
        <ImageLightbox
          src={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/${t.code}/receipt`}
          alt="Reçu de transaction"
          downloadHref={`${process.env.REACT_APP_BACKEND_URL}/api/transactions/${t.code}/receipt`}
          isPdf
          onClose={() => setReceiptOpen(false)}
        />
      )}
    </li>
  );
}
